import React, { useEffect, useState } from "react";
import logo from "../assets/images/Logo/web_logo.png";
import { EmailInput, PasswordInput, Checkbox } from "../components/form/inputs";
import PrimaryBtn from "../components/button/PrimaryBtn";
import { Link, useNavigate } from "react-router-dom";
import DecorativeSquares from "../components/ui/DecorativeSquares";
import { useDispatch, useSelector } from "react-redux";
import {
  registerUser,
  verifyOtp,
  setPassword,
  resendOtp,
} from "../redux/thunks/registerThunks";
import { prevStep } from "../redux/slices/registerSlice";
import toast from "react-hot-toast";

const Register = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { step, loading, email, error, user_id } = useSelector(
    (state) => state.register
  );

  // Form states
  const [useremail, setUseremail] = useState("");
  const [emailError, setEmailError] = useState("");

  const [otp, setOtp] = useState(["", "", "", ""]);
  const [otpError, setOtpError] = useState("");

  const [password, setPasswordState] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const [repassword, setRepasswordState] = useState("");
  const [repasswordError, setRepasswordError] = useState("");

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState("");

  const [seconds, setSeconds] = useState(120);
  const [isResendOtp, setIsResendOtp] = useState(false);

  const passwordRules = {
    uppercase: /[A-Z]/,
    lowercase: /[a-z]/,
    number: /[0-9]/,
    special: /[!@#$%^&*]/,
    length: /.{8,}/,
  };

  const checkRule = (rule) => rule.test(password);

  const handlePrev = () => {
    dispatch(prevStep());
  };

  const validateEmail = () => {
    if (!useremail) {
      setEmailError("Email is required");
      return false;
    } else if (!/^\S+@\S+\.\S+$/.test(useremail)) {
      setEmailError("Enter a valid email");
      return false;
    }
    setEmailError("");
    return true;
  };

  const validateOTP = () => {
    if (otp.some((digit) => digit === "")) {
      setOtpError("Complete the OTP");
      return false;
    }
    setOtpError("");
    return true;
  };

  const validatePassword = () => {
    if (!password) {
      setPasswordError("Password is required");
      return false;
    } else if (
      !checkRule(passwordRules.uppercase) ||
      !checkRule(passwordRules.lowercase) ||
      !checkRule(passwordRules.number) ||
      !checkRule(passwordRules.special) ||
      !checkRule(passwordRules.length)
    ) {
      setPasswordError("Password does not meet all requirements");
      return false;
    }
    setPasswordError("");
    return true;
  };

  const validateRePassword = () => {
    if (!repassword) {
      setRepasswordError("Please re-enter password");
      return false;
    } else if (password !== repassword) {
      setRepasswordError("Passwords do not match");
      return false;
    }
    setRepasswordError("");
    return true;
  };

  const validateTerms = () => {
    if (!termsAccepted) {
      setTermsError("You must accept the terms to continue");
      return false;
    }
    setTermsError("");
    return true;
  };

  const handleNext = () => {
    if (loading) return;

    if (step === 1) {
      if (validateEmail() && validateTerms()) {
        dispatch(
          registerUser({
            email: useremail,
            is_agree_to_policies: termsAccepted ? 1 : 0,
          })
        ).then((res) => {
          if (res.meta.requestStatus === "fulfilled") {
            toast.success(res.payload.message || "OTP sent successfully");
          } else {
            toast.error(res.payload || "Something went wrong");
          }
        });
      }
    }

    if (step === 2) {
      if (validateOTP()) {
        dispatch(
          verifyOtp({
            user_id: user_id,
            otp_type: "register_otp",
            otp: otp.join(""),
          })
        ).then((res) => {
          if (res.meta.requestStatus === "fulfilled") {
            toast.success(res.payload.message || "OTP verified");
          } else {
            toast.error(res.payload || "Invalid OTP");
          }
        });
      }
    }
  };

  useEffect(() => {
    if (seconds <= 0) return; // stop at 0

    const interval = setInterval(() => {
      setSeconds((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval); // cleanup
  }, [seconds]);

  const formatTime = (sec) => {
    const m = String(Math.floor(sec / 60)).padStart(2, "0");
    const s = String(sec % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  // Resend OTP handler
  const handleresendOtp = () => {
    if (seconds > 0) return; // button disabled check

    dispatch(
      resendOtp({
        user_id: user_id,
        otp_type: "register_otp",
      })
    ).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        toast.success(res.payload.message || "OTP resent successfully");
        setSeconds(120); // 🔄 reset timer
      } else {
        toast.error(res.payload || "Something went wrong");
      }
    });
  };

  const handleFinalSubmit = () => {
    if (validatePassword() && validateRePassword()) {
      dispatch(
        setPassword({
          user_id: user_id,
          password: password,
          password_confirmation: repassword,
          otp_type: "register_otp",
        })
      ).then((res) => {
        if (res.meta.requestStatus === "fulfilled") {
          // show toast here
          toast.success(res.payload.message || "Password set successfully");
          localStorage.setItem("user_id", user_id);
          localStorage.setItem("email", email);
          navigate("/set-up-profile");
        } else {
          toast.error(res.payload || "Something went wrong");
        }
      });
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 3) document.getElementById(`otp-${index + 1}`).focus();
    if (!value && index > 0)
      document.getElementById(`otp-${index - 1}`).focus();
  };

  const stepInstruction = () => {
    switch (step) {
      case 1:
        return {
          label: "Step 1",
          text: "Enter your email to receive an OTP and start setting up your Tiffin business.",
        };
      case 2:
        return {
          label: "Step 2",
          text: "Email Verification",
        };
      case 3:
        return {
          label: "Step 3",
          text: "Create a secure password for your account to complete registration.",
        };
      default:
        return { label: "", text: "" };
    }
  };

  const { label, text } = stepInstruction();

  const RuleItem = ({ valid, text }) => (
    <div className="flex items-center gap-2 text-sm text-[#4A4A4A]">
      <span
        className={`text-lg font-bold ${
          valid ? "text-[#FF6943]" : "text-gray-300"
        }`}
      >
        ✓
      </span>
      <span>{text}</span>
    </div>
  );

  return (
    <section className="min-h-screen relative overflow-hidden flex flex-col items-center justify-center py-8 px-4 sm:px-6 lg:px-20 bg-gray-50 space-y-6 lg:bg-gradient-to-br lg:from-orange-50 lg:to-orange-100">
      <DecorativeSquares />

      {/* Logo */}
      <div className="flex flex-col items-center justify-center py-4">
        <img src={logo} alt="web_logo" className="w-24 h-24 object-contain" />
        <p className="text-gray-400 text-center text-sm mt-4">
          You Cook, We Manage the Rest
        </p>
      </div>

      {/* Progress Bar */}
      <div className="flex justify-center mb-6 space-x-4">
        {[1, 2, 3].map((s) => (
          <div
            key={s}
            className={`w-20 h-1 rounded-full transition-colors duration-300 ${
              step >= s ? "bg-[#FF6943]" : "bg-gray-300"
            }`}
          ></div>
        ))}
      </div>

      {/* Mobile Step Instruction */}
      <div className="lg:hidden w-full text-center">
        <p className="text-sm font-semibold text-[#FF6943]">{label}</p>
        <h3 className="text-lg font-bold text-gray-900 mt-1">
          {step === 1
            ? "Enter Email"
            : step === 2
            ? "Verify OTP"
            : "Set Password"}
        </h3>
        <p className="text-sm text-gray-600 mt-2">{text}</p>
      </div>

      {/* Form Container */}
      <div className="w-full max-w-5xl bg-transparent md:bg-white rounded-2xl shadow-none md:shadow-lg flex flex-col lg:flex-row overflow-hidden">
        {/* Left Instructions */}
        <div className="hidden lg:flex flex-col justify-center text-white p-12 w-1/2 space-y-6 bg-[#FF6943]">
          <h2 className="text-3xl font-bold">
            {label}:{" "}
            {step === 1
              ? "Enter Email"
              : step === 2
              ? "Verify OTP"
              : "Set Password"}
          </h2>
          <p>{text}</p>
        </div>

        {/* Right Form */}
        <div className="w-full lg:w-1/2 p-8 sm:p-12 flex flex-col gap-6">
          {step === 1 && (
            <>
              <EmailInput
                label="Email Address"
                value={useremail}
                onChange={(e) => setUseremail(e.target.value)}
                placeholder="Enter your email"
                required
                error={emailError}
              />
              {error && <p className="text-xs text-red-500">{error}</p>}

              <div className="flex gap-2 items-center">
                <Checkbox
                  checked={termsAccepted}
                  onChange={() => setTermsAccepted(!termsAccepted)}
                />
                <p className="text-sm text-[#232323]">
                  I agree to the{" "}
                  <Link
                    to={"/privacy"}
                    className="text-[#FF6943] cursor-pointer hover:underline"
                  >
                    Privacy Policy
                  </Link>{" "}
                  and{" "}
                  <Link
                    to={"/terms-and-conditions"}
                    className="text-[#FF6943] cursor-pointer hover:underline"
                  >
                    Terms of Service
                  </Link>
                </p>
              </div>
              {termsError && (
                <p className="text-xs text-red-500">{termsError}</p>
              )}
            </>
          )}

          {step === 2 && (
            <>
              <p className="text-gray-600 text-center">
                Enter the OTP sent to your email:
              </p>
              <div className="flex justify-center gap-3">
                {otp.map((value, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    maxLength={1}
                    value={value}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    className={`w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 text-center border rounded-lg text-lg text-gray-700 focus:outline-none transition-colors duration-150 ${
                      value
                        ? "bg-[#FF6943] text-white border-[#FF6943]"
                        : "bg-white border-gray-300 text-gray-700"
                    }`}
                  />
                ))}
              </div>

              <div className="space-y-5 text-center">
                <p className="text-[#4A4A4A] text-base">
                  A one-time password has been sent to{" "}
                  <span className="text-black">{email}</span>
                </p>
                <p className="text-[#000000] text-base ">
                  Resend OTP in {formatTime(seconds)}
                </p>
                <button
                  className={
                    seconds <= 0
                      ? "text-[#FF6943] text-base cursor-pointer underline"
                      : "text-gray-400 text-base cursor-not-allowed"
                  }
                  onClick={handleresendOtp}
                  disabled={seconds > 0} // disable button while timer is running
                >
                  Resend OTP
                </button>

                <p className="text-[#8B8B8B] text-base">
                  Want to change your email address?
                </p>
                <p
                  className="text-[#FE724E] text-base cursor-pointer"
                  onClick={handlePrev}
                >
                  Change Here
                </p>
              </div>
              {otpError && (
                <p className="text-xs text-red-500 text-center mt-2">
                  {otpError}
                </p>
              )}
            </>
          )}

          {step === 3 && (
            <>
              <PasswordInput
                label="Password"
                value={password}
                onChange={(e) => setPasswordState(e.target.value)}
                placeholder="Enter Your Password"
                required
                showPasswordToggle
                error={passwordError}
              />

              <PasswordInput
                label="Re-enter password"
                value={repassword}
                onChange={(e) => setRepasswordState(e.target.value)}
                placeholder="Re-enter password"
                required
                showPasswordToggle
                error={repasswordError}
              />

              <div className="space-y-3 mt-2">
                <RuleItem
                  valid={checkRule(passwordRules.uppercase)}
                  text="At least one uppercase letter (A-Z)"
                />
                <RuleItem
                  valid={checkRule(passwordRules.lowercase)}
                  text="At least one lowercase letter (a-z)"
                />
                <RuleItem
                  valid={checkRule(passwordRules.number)}
                  text="At least one number (0-9)"
                />
                <RuleItem
                  valid={checkRule(passwordRules.special)}
                  text="At least one special character (!@#$%^&*)"
                />
                <RuleItem
                  valid={checkRule(passwordRules.length)}
                  text="Minimum 8 characters"
                />
              </div>
            </>
          )}

          <div className="flex flex-col justify-center mt-4">
            <PrimaryBtn
            extraClass="w-full"
              btnName={
                loading ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processing...
                  </span>
                ) : step === 3 ? (
                  "Submit"
                ) : (
                  "Next"
                )
              }
              clickEvent={step === 3 ? handleFinalSubmit : handleNext}
              disabled={loading}
            />

            {step === 1 && (
              <p className="text-center text-sm text-gray-500 mt-8">
                Already have an account?{" "}
                <Link
                  to={"/login"}
                  className="text-[#FF6943] cursor-pointer hover:underline"
                >
                  Log in
                </Link>
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Register;
